/*
  This file contains the default state and info of all things this app:
    Class Moves
    Spells
    Damage
    Max-Weight
*/
const defaultState = {
  characterClasses: [
    "barbarian",
    "bard",
    "cleric",
    "druid",
    "fighter",
    "immolator",
    "paladin",
    "thief",
    "wizard",
    "channeler"
  ],
  classSepcs: {
    barbarian: {
      maxWeight: 8,
      damage: "D10"
    },
    bard: {
      maxWeight: 9,
      damage: "D6"
    },
    cleric: {
      maxWeight: 10,
      damage: "D6",
      spells: {
        rotes: [
          {
            title: `Light`,
            type: `rotes`,
            html: `<p><em>Rote</em></p><p>An item you touch glows with divine light, about as bright as a torch. It gives off no heat or sound and requires no fuel, but it is otherwise like a mundane torch. You have complete control of the color of the flame. The spell lasts as long as it is in your presence.</p>`,
            have: false
          },
          {
            title: `Sanctify`,
            type: `rotes`,
            html: `<p><em>Rote</em></p><p>Food or water you hold in your hands while you cast this spell is consecrated by your deity. In addition to now being holy or unholy, the affected substance is purified of any mundane spoilage.</p>`,
            have: false
          },
          {
            title: `Guidance`,
            type: `rotes`,
            html: `<p><em>Rote</em></p><p>The symbol of your deity appears before you and gestures towards the direction or course of action your deity would have you take then disappears. The message is through gesture only; your communication through this spell is severely limited.</p>`,
            have: false
          }
        ],
        // Important note, this variable is refered to as the actual spell name (use exact name camel case)
        firstLevelSpells: [
          {
            title: `Bless`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level (ongoing)</em></p><p>Your deity smiles upon a combatant of your choice. They take +1 ongoing so long as battle continues and they stand and fight. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Cure Light Wounds`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level</em></p><p>At your touch wounds scab and bones cease to ache. Heal an ally you touch of 1d8 damage.</p>`,
            have: false
          },
          {
            title: `Detect Alignment`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level</em></p><p>When you cast this spell choose an alignment: Good, Evil, Lawful, or Chaotic. One of your senses is briefly able to detect that alignment. The GM will tell you what here is of that alignment.</p>`,
            have: false
          },
          {
            title: `Cause Fear`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level (ongoing)</em></p><p>Choose a target you can see and a nearby object. The target is afraid of the object so long as you maintain the spell. Their reaction is up to them: flee, panic, beg, fight. While this spell is ongoing you take -1 to cast a spell. You cannot target entities with less than animal intelligence (magical constructs, undead, automatons, and the like).</p>`,
            have: false
          },
          {
            title: `Magic Weapon`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level (ongoing)</em></p><p>The weapon you hold while casting does +1d4 damage until you dismiss this spell. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Sanctuary`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level</em></p><p>As you cast this spell, you walk the perimeter of an area, consecrating it to your deity. As long as you stay within that area you are alerted whenever someone acts with malice within the sanctuary (including entering with harmful intent). Anyone who receives healing within a sanctuary heals +1d4 HP.</p>`,
            have: false
          },
          {
            title: `Speak With Dead`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level</em></p><p>A corpse converses with you briefly. It will answer any three questions you pose to it to the best of the knowledge it had in life and the knowledge it gained in death.</p>`,
            have: false
          }
        ],
        thirdLevelSpells: [
          {
            title: `Animate Dead`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level (ongoing)</em></p><p>You invoke a hungry spirit to possess a recently-dead body and serve you. This creates a zombie that follows your orders to the best of its limited abilities. Treat the zombie as a character, but with access to only the basic moves. It has a +1 modifier for all stats and 1 HP. The zombie also gets your choice of 1d4 of these traits:</p><li>It’s talented. Give one stat a +2 modifier.</li><li>It’s durable. It has +2 HP for each level you have.</li><li>It has a functioning brain and can complete complex tasks. </li><li>It does not appear obviously dead, at least for a day or two.</li><p>The zombie lasts until it is destroyed by taking damage in excess of its HP, or until you end the spell. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Resurrection`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level</em></p><p>Tell the GM you would like to resurrect a corpse whose soul has not yet fully departed this world. Resurrection is always possible, but the GM will give you one or more (possibly all) of these conditions to fulfill:</p><li>It’s going to take days/weeks/months </li><li>You must get help from ____</li><li>It will require a lot of money</li><li>You must sacrifice ____ to do it</li><p>The GM may, depending on the circum- stances, allow you to resurrect the corpse now, with the understanding that the conditions must be met before it’s permanent, or require you to meet the conditions before the corpse is resurrected.</p>`,
            have: false
          },
          {
            title: `Cure Moderate Wounds`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level</em></p><p>You staunch bleeding and set bones through magic. Heal an ally you touch of 2d8 damage.</p>`,
            have: false
          },
          {
            title: `Darkness`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level (ongoing)</em></p><p>Choose an area you can see: it’s filled with supernatural darkness and shadow. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Hold Person`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level</em></p><p>Choose a person you can see. Until you cast a spell or leave their presence they cannot act except to speak. This effect ends immediately if the target takes damage from any source.</p>`,
            have: false
          }
        ],
        fifthLevelSpells: [
          {
            title: `Revelation`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level</em></p><p>Your deity answers your prayers with a moment of perfect understanding. The GM will shed light on the current situation. When acting on the information, you take +1 forward.</p>`,
            have: false
          },
          {
            title: `Cure Critical Wounds`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level</em></p><p>Heal an ally you touch of 3d8 damage.</p>`,
            have: false
          },
          {
            title: `Divination`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level</em></p><p>Name a person, place, or thing you want to learn about. Your deity grants you visions of the target, as clear as if you were there.</p>`,
            have: false
          },
          {
            title: `True Seeing`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level (ongoing)</em></p><p>Your vision is opened to the true nature of everything you lay your eyes on. You pierce illusions and see things that have been hidden. The GM will describe the area before you ignoring any illusions and falsehoods, magical or otherwise. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Contagion`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level (ongoing)</em></p><p>Choose a creature you can see. Until you end this spell, the target suffers from a disease of your choice. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Trap Soul`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level</em></p><p>You trap the soul of a dying creature within a gem. The trapped creature is aware of its imprisonment but can still be manipulated through spells, parley, and other effects. All moves against the trapped creature are at +1. You can free the soul at any time but it can never be recaptured once freed.</p>`,
            have: false
          },
          {
            title: `Words Of The Unspeaking`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level</em></p><p>With a touch you speak to the spirits within things. The non-living object you touch answers three questions you pose, as best it can.</p>`,
            have: false
          }
        ],
        seventhLevelSpells: [
          {
            title: `Word Of Recall`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level</em></p><p>Choose a word. The first time after casting this spell that you speak the chosen word, you and any allies touching you when you cast the spell are immediately returned to the exact spot where you cast the spell. You can only maintain a single location; casting Word of Recall again before speaking the word replaces the earlier spell.</p>`,
            have: false
          },
          {
            title: `Heal`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level</em></p><p>Touch an ally and you may heal their damage a number of points up to your maximum HP.</p>`,
            have: false
          },
          {
            title: `Harm`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level</em></p><p>Touch an enemy and strike them with divine wrath—deal 2d8 damage to them and 1d6 damage to yourself. This damage ignores armor.</p>`,
            have: false
          },
          {
            title: `Sever`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level (ongoing)</em></p><p>Choose an appendage on the target such as an arm, tentacle, or wing. The appendage is magically severed from their body, causing no damage but considerable pain. Missing an appendage may, for example, keep a winged creature from flying, or a bull from goring you on its horns. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Mark Of Death`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level</em></p><p>Choose a creature whose true name you know. This spell creates permanent runes on a target surface that will kill that creature, should they read them.</p>`,
            have: false
          },
          {
            title: `Control Weather`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level</em></p><p>Pray for rain—or sun, wind, or snow. Within a day or so, your god will answer. The weather will change according to your will and last a handful of days.</p>`,
            have: false
          }
        ],
        ninthLevelSpells: [
          {
            title: `Storm Of Vengeance`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level</em></p><p>Your deity brings the unnatural weather of your choice to pass. Rain of blood or acid, clouds of souls, wind that can carry away buildings, or any other weather you can imagine: ask and it shall come.</p>`,
            have: false
          },
          {
            title: `Repair`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level</em></p><p>Choose one event in the target’s past. All effects of that event, including damage, poison, disease, and magical effects, are ended and repaired. HP and diseases are healed, poisons are neutralized, magical effects are ended.</p>`,
            have: false
          },
          {
            title: `Consume Unlife`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level</em></p><p>The mindless undead creature you touch is destroyed and you steal its death energy to heal yourself or the next ally you touch. The amount of damage healed is equal to the HP that the creature had remaining before you destroyed it.</p>`,
            have: false
          },
          {
            title: `Plague`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level (ongoing)</em></p><p>Name a city, town, encampment, or other place where people live. As long as this spell is active that place is beset by a plague appropriate to your deity’s domains (locusts, death of the first born, etc.) While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Divine Presence`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level (ongoing)</em></p><p>Every creature must ask your leave to enter your presence, and you must give permission aloud for them to enter. Any creature without your leave takes an extra 1d10 damage whenever they take damage in your presence. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          }
        ]
      }
    },
    druid: {
      maxWeight: 6,
      damage: "D6"
    },
    fighter: {
      maxWeight: 12,
      damage: "D10"
    },
    immolator: {
      maxWeight: 9,
      damage: "D8"
    },
    paladin: {
      maxWeight: 12,
      damage: "D10"
    },
    thief: {
      maxWeight: 9,
      damage: "D8"
    },
    wizard: {
      maxWeight: 6,
      damage: "D4",
      spells: {
        catrips: [
          {
            title: `Light`,
            type: `catrips`,
            html: `<p><em>Cantrip</em></p><p>An item you touch glows with arcane light, about as bright as a torch. It gives off no heat or sound and requires no fuel, but it is otherwise like a mundane torch. You have complete control of the color of the flame. The spell lasts as long as it is in your presence.</p>`,
            have: false
          },
          {
            title: `Unseen Servant`,
            type: `catrips`,
            html: `<p><em>Cantrip</em></p><p>You conjure a simple invisible construct that can do nothing but carry items. It has Load 3 and carries anything you hand to it. It cannot pick up items on its own and can only carry those you give to it. Items carried by an unseen servant appear to float in the air a few paces behind you. An unseen servant that takes damage or leaves your presence is immediately dispelled, dropping any items it carried.</p>`,
            have: false
          },
          {
            title: `Prestidigitation`,
            type: `catrips`,
            html: `<p><em>Cantrip</em></p><p>You perform minor tricks of true magic. If you touch an item as part of the casting you can make cosmetic changes to it: clean it, soil it, cool it, warm it, flavor it, or change its color. If you cast the spell without touching an item you can instead create minor illusions no bigger than yourself. Prestidigitation illusions are crude and clearly illusions—they won’t fool anyone, but they might entertain them.</p>`,
            have: false
          }
        ],
        // Important note, this variable is refered to as the actual spell name (use exact name camel case)
        firstLevelSpells: [
          {
            title: `Contact Spirits`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level Summoning</em></p><p>Name the spirit you wish to contact (or leave it to the GM). You pull that creature through the planes, just close enough to speak to you. It is bound to answer any one question you ask to the best of its ability.</p>`,
            have: false
          },
          {
            title: `Detect Magic`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level Divination</em></p><p>One of your senses is briefly attuned to magic. The GM will tell you what here is magical.</p>`,
            have: false
          },
          {
            title: `Magic Missile`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level Evocation</em></p><p>Projectiles of pure magic spring from your fingers. Deal 2d4 damage to one target.</p>`,
            have: false
          },
          {
            title: `Charm Person`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level Enchantment (ongoing)</em></p><p>The person (not beast or monster) you touch while casting this spell counts you as a friend until they take damage or you prove otherwise.</p>`,
            have: false
          },
          {
            title: `Invisibility`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level Illusion (ongoing)</em></p><p>Touch an ally: nobody can see them. They’re invisible! The spell persists until the target attacks or you dismiss the effect. While the spell is ongoing you can’t cast a spell.</p>`,
            have: false
          },
          {
            title: `Telepathy`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level Divination (ongoing)</em></p><p>You form a telepathic bond with a single person you touch, enabling you to converse with that person through your thoughts. You can only have one telepathic bond at a time.</p>`,
            have: false
          },
          {
            title: `Alarm`,
            type: `firstLevelSpells`,
            html: `<p><em>1st Level</em></p><p>Walk a wide circle as you cast this spell. Until you prepare spells again your magic will alert you if a creature crosses that circle. Even if you are asleep, the spell will shake you from your slumber.</p>`,
            have: false
          }
        ],
        thirdLevelSpells: [
          {
            title: `Dispel magic`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level</em></p><p>Choose a spell or magic effect in your presence: this spell rips it apart. Lesser spells are ended, powerful magic is just reduced or dampened so long as you are nearby.</p>`,
            have: false
          },
          {
            title: `Visions Through Time`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level Divination</em></p><p>Cast this spell and gaze into a reflective surface to see into the depths of time. The GM will reveal the details of a grim portent to you—a bleak event that will come to pass without your intervention. They’ll tell you something useful about how you can interfere with the grim portent’s dark outcomes. Rare is the portent that claims “You’ll live happily ever after.” Sorry.</p>`,
            have: false
          },
          {
            title: `Fireball`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level Evocation</em></p><p>You evoke a mighty ball of flame that envelops your target and everyone nearby, inflicting 2d6 damage which ignores armor.</p>`,
            have: false
          },
          {
            title: `Mimic`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level (ongoing)</em></p><p>You take the form of someone you touch while casting this spell. Your physical characteristics match theirs exactly but your behavior may not. This change persists until you take damage or choose to return to your own form. While this spell is ongoing you lose access to all your wizard moves.</p>`,
            have: false
          },
          {
            title: `Mirror Image`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level Illusion</em></p><p>You create an illusory image of yourself. When you are attacked, roll a d6. On a 4, 5, or 6 the attack hits the illusion instead, the image then dissipates and the spell ends.</p>`,
            have: false
          },
          {
            title: `Sleep`,
            type: `thirdLevelSpells`,
            html: `<p><em>3rd Level Enchantment</em></p><p>1d4 enemies you can see of the GM’s choice fall asleep. Only creatures capable of sleeping are affected. They awake as normal: loud noises, jolts, pain.</p>`,
            have: false
          }
        ],
        fifthLevelSpells: [
          {
            title: `Cage`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level Evocation (ongoing)</em></p><p>The target is held in a cage of magical force. Nothing can get in or out of the cage. The cage remains until you cast another spell or dismiss it. While the spell is ongoing, the caged creature can hear your thoughts and you cannot leave sight of the cage.</p>`,
            have: false
          },
          {
            title: `Contact Other Plane`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level Divination</em></p><p>You send a request to another plane. Specify who or what you’d like to contact by location, type of creature, name, or title. You open a two-way communication with that creature. Your communication can be cut off at any time by you or the creature you contacted.</p>`,
            have: false
          },
          {
            title: `Summon Monster`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level Summoning (ongoing)</em></p><p>A monster appears and aids you as best it can. Treat it as your character, but with access to only the basic moves. It has +1 modifier for all stats, 1 HP, and uses your damage dice. The monster also gets your choice of 1d6 of these traits:</p><li>It has +2 instead of +1 to one stat</li><li>It’s not reckless</li><li>It does 1d8 damage</li><li>Its bond to your plane is strong: +2 HP for each level you have</li><li>It has some useful adaptation</li><p>The GM will tell you the type of monster you get based on the traits you select. The creature remains on this plane until it dies or you dismiss it. While the spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Polymorph`,
            type: `fifthLevelSpells`,
            html: `<p><em>5th Level Enchantment</em></p><p>Your touch reshapes a creature entirely, they stay in the form you craft until you cast a spell. Describe the new shape you craft, including any stat changes, significant adaptations, or major weaknesses. The GM will then tell you one or more of these:</p><li>The form will be unstable and temporary </li><li>The creature’s mind will be altered as well</li><li>The form has an unintended benefit or weakness</li>`,
            have: false
          }
        ],
        seventhLevelSpells: [
          {
            title: `Contingency`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level Evocation</em></p><p>Choose a 5th level or lower spell you know. Describe a trigger condition using a number of words equal to your level. The chosen spell is held until you choose to unleash it or the trigger condition is met, whichever happens first. You don’t have to roll for the held spell, it just takes effect. You may only have a single contingent spell held at a time; if you cast Contingency while you have a held spell, the new held spell replaces the old one.</p>`,
            have: false
          },
          {
            title: `True Seeing`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level Divination (ongoing)</em></p><p>You see all things as they truly are. This effect persists until you tell a lie or dismiss the spell. While this spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          },
          {
            title: `Shadow Walk`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level Illusion</em></p><p>The shadows you target with this spell become a portal for you and your allies. Name a location, describing it with a number of words up to your level. Stepping through the portal deposits you and any allies present when you cast the spell at the location you described. The portal may only be used once by each ally.</p>`,
            have: false
          },
          {
            title: `Cloudkill`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level Summoning (ongoing)</em></p><p>A cloud of fog drifts into this realm from beyond the Black Gates of Death, filling the immediate area. Whenever a creature in the area takes damage it takes an additional, separate 1d6 damage which ignores armor. This spell persists so long as you can see the affected area, or until you dismiss it.</p>`,
            have: false
          },
          {
            title: `Dominate`,
            type: `seventhLevelSpells`,
            html: `<p><em>7th Level Enchantment (ongoing)</em></p><p>Your touch pushes your mind into someone else’s. You gain 1d4 hold. Spend one hold to make the target take one of these actions:</p><li>Speak a few words of your choice</li><li>Give you something they hold</li><li>Make a concerted attack on a target of your choice</li><li>Truthfully answer one question</li><p>If you run out of hold the spell ends. If the target takes damage you lose 1 hold. While the spell is ongoing you cannot cast a spell.</p>`,
            have: false
          }
        ],
        ninthLevelSpells: [
          {
            title: `Alert`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level Divination</em></p><p>Describe an event. The GM will tell you when that event occurs, no matter where you are or how far away the event is. If you choose, you can view the location of the event as though you were there in person. You can only have one Alert active at a time.</p>`,
            have: false
          },
          {
            title: `Soul Gem`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level</em></p><p>You trap the soul of a dying creature within a gem. The trapped creature is aware of its imprisonment but can still be manipulated through spells, parley, and other effects. All moves against the trapped creature are at +1. You can free the soul at any time but it can never be recaptured once freed.</p>`,
            have: false
          },
          {
            title: `Shelter`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level Evocation (ongoing)</em></p><p>You create a structure out of pure magical power. It can be as large as a castle or as small as a hut, but is impervious to all non-magical damage. The structure endures until you leave it or you end the spell.</p>`,
            have: false
          },
          {
            title: `Perfect Summons`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level Summoning</em></p><p>You teleport a creature to your presence. Name a creature or give a short description of a type of creature. If you named a creature, that creature appears before you. If you described a type of creature, a creature of that type appears before you.</p>`,
            have: false
          },
          {
            title: `Antipathy`,
            type: `ninthLevelSpells`,
            html: `<p><em>9th Level Enchantment (ongoing)</em></p><p>Choose a target and describe a type of creature or an alignment. Creatures of the specified type or alignment cannot come within sight of the target. If a creature of the specified type does find itself within sight of the target, it immediately flees. This effect continues until you leave the target’s presence or you dismiss the spell. While the spell is ongoing you take -1 to cast a spell.</p>`,
            have: false
          }
        ]
      }
    }
  },
  races: [
    { title: "Human", description: "This is a placeholder for human" },
    { title: "Halfling", description: "This is a placeholder for Halfling" },
    { title: "Orc", description: "This is a placeholder for Orc" },
    { title: "Other", description: "" }
  ],
  moves: {
    basic: [
      {
        title: `Hack & Slash`,
        type: `basic`,
        html: `<p>When you attack an enemy in melee, roll+Str. On a 10+ you deal your damage to the enemy and avoid their attack. At your option, you may choose to do +1d6 damage but expose yourself to the enemy’s attack. On a 7–9, you deal your damage to the enemy and the enemy makes an attack against you.</p>`,
        have: null
      },
      {
        title: `Volley`,
        type: `basic`,
        html: `<p>When you take aim and shoot at an enemy at range, roll+Dex. On a 10+ you have a clear shot—deal your damage. On a 7–9, choose one (whichever you choose you deal your damage):</p><li>You have to move to get the shot placing you in danger of the GM’s choice</li><li>You have to take what you can get: -1d6 damage</li><li>You have to take several shots, reducing your ammo by one.</li>`,
        have: null
      },
      {
        title: `Parley`,
        type: `basic`,
        html: `<p>When you have leverage on a GM character and manipulate them, roll+Cha. Leverage is something they need or want. On a hit they ask you for something and do it if you make them a promise first. On a 7–9, they need some concrete assurance of your promise, right now.</p>`,
        have: null
      },
      {
        title: `Defy Danger`,
        type: `basic`,
        html: `<p>When you act despite an imminent threat</p><p>or suffer a calamity, say how you deal with it and roll. If you do it...</p><li>by powering through, +Str</li><li>by getting out of the way or acting fast, +Dex</li><li>by enduring, +Con</li><li>with quick thinking, +Int</li><li>through mental fortitude, +Wis</li><li>sing charm and social grace, +Cha</li><p>On a 10+, you do what you set out to, the threat doesn’t come to bear. On a 7–9, you stumble, hesitate, or flinch: the GM will offer you a worse outcome, hard bargain, or ugly choice.</p>`,
        have: null
      },
      {
        title: `Defend`,
        type: `basic`,
        html: `<p>When you stand in defense of a person, item, or location under attack, roll+Con. On a 10+, hold 3. On a 7–9, hold 1. So long as you stand in defense, when you or the thing you defend is attacked you may spend hold, 1 for 1, to choose an option:</p><li>Redirect an attack from the thing you defend to yourself</li><li>Halve the attack’s effect or damage</li><li>Open up the attacker to an ally giving that ally +1 forward against the attacker </li><li>Deal damage to the attacker equal to your level</li>`,
        have: null
      },
      {
        title: `Discern Realities`,
        type: `basic`,
        html: `<p>When you closely study a situation or person, roll+Wis. On a 10+ ask the GM three questions from the list below. On a 7–9 ask only one. Take +1 forward when acting on the answers.</p><li>What happened here recently?</li><li>What is about to happen?</li><li>What should I be on the lookout for?</li><li>What here is useful or valuable to me?</li></li><li>Who’s really in control here?</li><li>What here is not what it appears to be?</li>`,
        have: null
      },
      {
        title: `Spout Lore`,
        type: `basic`,
        html: `<p>When you consult your accumulated knowledge about something, roll+Int. On a 10+ the GM will tell you something interesting and useful about the subject relevant to your situation. On a 7–9 the GM will only tell you something interest- ing—it’s on you to make it useful. The GM might ask you “How do you know this?” Tell them the truth, now.</p>`,
        have: null
      }
    ],
    special: [
      {
        title: `Last Breath`,
        type: `special`,
        html: `<p>When you’re dying you catch a glimpse of what lies beyond the Black Gates of Death’s Kingdom (the GM will describe it). Then roll (just roll, +nothing—yeah, Death doesn’t care how tough or cool you are). On a 10+ you’ve cheated death— you’re in a bad spot but you’re still alive. On a 7–9 Death will offer you a bargain. Take it and stabilize or refuse and pass beyond the Black Gates into whatever fate awaits you. On a miss, your fate is sealed. You’re marked as Death’s own and you’ll cross the threshold soon. The GM will tell you when.</p>`,
        have: null
      },
      {
        title: `Encumbrance`,
        type: `special`,
        html: `<p>When you make a move while carrying weight up to or equal to Load, you’re fine. When you make a move while carrying weight equal to load+1 or load+2, you take -1. When you make a move while carrying weight greater than load+2, you have a choice: drop at least 1 weight and roll at -1, or automatically fail.</p>`,
        have: null
      },
      {
        title: `Make Camp`,
        type: `special`,
        html: `<p>When you settle in to rest consume a ration; If you’re somewhere dangerous decide the watch order as well. If you have enough XP you may Level Up. When you wake from at least a few uninterrupted hours of sleep heal damage equal to half your max HP.</p>`,
        have: null
      },
      {
        title: `Take Watch`,
        type: `special`,
        html: `<p>When you’re on watch and something approaches the camp, roll+Wis. On a 10+ you’re able to wake the camp and prepare a response, the camp takes +1 forward. On a 7–9 you react just a moment too late; the camp is awake but hasn’t had time to prepare. You have weapons and armor but little else. On a miss whatever lurks outside the campfire’s light has the drop on you.</p>`,
        have: null
      },
      {
        title: `Bolster`,
        type: `special`,
        html: `<p>When you spend your leisure time instudy, meditation, or hard practice, you gain preparation. If you prepare for a week or two, 1 preparation. If you prepare for a month or longer, 3 preparation. When your preparation pays off spend 1 preparation for +1 to any roll. You can only spend one preparation per roll.</p>`,
        have: null
      },
      {
        title: `Supply`,
        type: `special`,
        html: `<p>When you go to buy something with money on hand, if it’s something readily available in the settlement you’re in, you can buy it at market price. If it’s something special, beyond what’s usually available here, or non-mundane, roll+Cha. On a 10+ you find what you’re looking for at a fair price. On a 7–9 you’ll have to pay more or settle for something similar.</p>`,
        have: null
      },
      {
        title: `Undertake A Perilous Journey`,
        type: `special`,
        html: `<p>When you travel through hostile territory, choose one member of the party to act as trailblazer, one to scout ahead, and one to be quartermaster (the same character cannot have two jobs). If you don’t have enough party members or choose not to assign a job, treat that job as if it had rolled a 6. Each character with a job to do rolls+Wis. On a 10+ the quartermaster reduces the number of rations required by one. On a 10+ the trailblazer reduces the amount of time it takes to reach your destination (the GM will say by how much). On a 10+ the scout will spot any trouble quick enough to let you get the drop on it. On a 7–9 each roles performs their job as expected: the normal number of rations are consumed, the journey takes about as long as expected, no one gets the drop on you but you don’t get the drop on them either.</p>`,
        have: null
      },
      {
        title: `Recover`,
        type: `special`,
        html: `<p>When you do nothing but rest in comfort and safety after a day of rest you recover all your HP. After three days of rest you remove one debility of your choice. If you’re under the care of a healer (magical or otherwise) you heal a debility for every two days of rest instead.</p>`,
        have: null
      },
      {
        title: `Carouse`,
        type: `special`,
        html: `<p>When you return triumphant and throw a</p><p>big party, spend 100 coin and roll + extra 100s of coin spent. On a 10+ choose 3. On a 7–9 choose 1. On a miss, you still choose one, but things get really out of hand.</p><li>You befriend a useful NPC</li><li>You hear rumors of an opportunity </li><li>You gain useful information</li><li>You are not entangled, ensorcelled, or tricked</li>`,
        have: null
      },
      {
        title: `Recruit`,
        type: `special`,
        html: `<p>When you put out word that you’re looking to hire help, roll. If you make it known...</p><li>...that your pay is generous, take +1</li><li>...what you’re setting out to do, take +1 </li><li>...that they’ll get a share of whatever you find, take +1</li><p>If you have a useful reputation around these parts take an additional +1. On a 10+ you’ve got your pick of a number of skilled applicants, your choice who you hire, no penalty for not taking them along. On a 7–9 you’ll have to settle for someone close or turn them away. On a miss someone influential and ill-suited declares they’d like to come along (a foolhardy youth, a loose-cannon, or a veiled enemy, for example), bring them and take the consequences or turn them away. If you turn away applicants you take -1 forward to Recruit.</p>`,
        have: null
      },
      {
        title: `End Of Session`,
        type: `special`,
        html: `<p>When you reach the end of a session, choose one your bonds that you feel is resolved (completely explored, no longer relevant, or otherwise). Ask the player of the character you have the bond with if they agree. If they do, mark XP and write a new bond with whomever you wish.</p><p>Once bonds have been updated look at your alignment. If you fulfilled that alignment at least once this session, mark XP. Then answer these three questions as a group:</p><li>Did we learn something new and important about the world?</li><li>Did we overcome a notable monster or enemy?</li><li>Did we loot a memorable treasure?</li><p>For each “yes” answer everyone marks XP.</p>`,
        have: null
      },
      {
        title: `Level Up`,
        type: `special`,
        html: `<p>When you have downtime (hours or days) and XP equal to (or greater than) your current level+7, subtract your current level +7 from your XP, increase your level by 1, and choose a new advanced move from your class. If you are the wizard, you also get to add a new spell to your spellbook. Choose one of your stats and increase it by 1 (this may change your modifier). Note that changing your Constitution increases your maximum and current HP. Ability scores can’t go higher than 18.</p>`,
        have: null
      }
    ],
    barbarian: {
      starting: [
        {
          title: `Full Plate And Packing Steel`,
          type: `starting`,
          html: `<p>You ignore the clumsy tag on armor you wear.</p>`,
          have: false
        },
        {
          title: `Unencumbered, Unharmed`,
          type: `starting`,
          html: `<p>So long as you are below your Load and neither wear armor nor carry a shield, take +1 armor.</p>`,
          have: false
        },
        {
          title: `The Upper Hand`,
          type: `starting`,
          html: `<p>You take +1 ongoing to last breath rolls. When you take your last breath, on a 7–9 you make an offer to Death in return for your life. If Death accepts he will return you to life. If not, you die.</p>`,
          have: null
        },
        {
          title: `What Are You Waiting For? `,
          type: `starting`,
          html: `<p>When you cry out a challenge to your enemies, roll+Con. • On a 10+ they treat you as the most obvious threat to be dealt with and ignore your companions, take +2 damage ongoing against them. • On a 7–9 only a few (the weakest or most foolhardy among them) fall prey to your taunting.</p>`,
          have: null
        },
        {
          title: `Herculean Appetites`,
          type: `starting`,
          html: `<p>Others may content themselves with just a taste of wine, or dominion over a servant or two, but you want more. Choose two appetites. While pursuing one of your appetites if you would roll for a move, instead of rolling 2d6 you roll 1d6+1d8. If the d6 is the higher die of the pair, the GM will also introduce a complication or danger that comes about due to your heedless pursuits.</p><p style="margin-left: 1em;">Pure destruction </p><p style="margin-left: 1em;">Power over others </p><p style="margin-left: 1em;">Mortal pleasures </p><p style="margin-left: 1em;">Conquest</p><p style="margin-left: 1em;">Riches and property </p><p style="margin-left: 1em;">Fame and glory</p>`,
          have: null
        },
        {
          title: `Musclebound`,
          type: `starting`,
          html: `<p>While you wield a weapon it gains the forceful and messy tags.</p>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Still Hungry`,
          type: `advanced`,
          html: `<p>Choose an additional appetite.</p>`,
          have: false
        },
        {
          title: `Appetite For Destruction`,
          type: `advanced`,
          html: `<p>Take a move from the Fighter, Bard, or Thief class list. You may not take multiclass moves from those classes.</p>`,
          have: false
        },
        {
          title: `My Love For You Is Like A Truck`,
          type: `advanced`,
          html: `<p>When you perform a feat of strength, name someone present whom you have impressed and take +1 forward to parley with them.</p>`,
          have: false
        },
        {
          title: `What Is Best In Life`,
          type: `advanced`,
          html: `<p>At the end of a session, if during this session you have crushed your enemies, seen them driven before you, or have heard the lamentations of their kinfolk mark XP.</p>`,
          have: false
        },
        {
          title: `Wide-Wanderer`,
          type: `advanced`,
          html: `<p>You’ve travelled the wide world over. When you arrive someplace ask the GM about any important traditions, rituals, and so on, they’ll tell you what you need to know.</p>`,
          have: false
        },
        {
          title: `Usurper`,
          type: `advanced`,
          html: `<p>When you prove yourself superior to a person in power, take +1 forward with their followers, underlings, and hangers on.</p>`,
          have: false
        },
        {
          title: `Khan Of Khans`,
          type: `advanced`,
          html: `<p>Your hirelings always accept the gratuitous fulfillment of one of your appetites as payment.</p>`,
          have: false
        },
        {
          title: `Samson`,
          type: `advanced`,
          html: `<p>You may take a debility to immediately break free of any physical or mental restraint.</p>`,
          have: false
        },
        {
          title: `Smash!`,
          type: `advanced`,
          html: `<p>When you Hack & Slash, on a 12+ deal your damage and choose something physical your target has (a weapon, their position, a limb): they lose it.</p>`,
          have: false
        },
        {
          title: `Indestructible Hunger`,
          type: `advanced`,
          html: `<p>When you take damage you can choose to take -1 ongoing until you sate one of your appetites instead of taking the damage. If you already have this penalty you cannot choose this option.</p>`,
          have: false
        },
        {
          title: `Eye For Weakness`,
          type: `advanced`,
          html: `<p>When you Discern Realities add "What here is weak or vulnerable?" to the list of questions you can ask.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `A Good Day To Die`,
          type: `expert`,
          html: `<p>As long as you have less than your CON in current HP (or 1, whichever is higher) take +1 ongoing.</p>`,
          have: false
        },
        {
          title: `Kill ‘Em All`,
          type: `expert`,
          html: `<p>(Requires: Appetite for Destruction) Take another move fromt he Fighter, Bard, or Thief class list. You may not take multiclass moves from those classes.</p>`,
          have: false
        },
        {
          title: `War Cry`,
          type: `expert`,
          html: `<p>When you enter the battle with a show of force, roll +CHA. • On a 10+ both, • on a 7-9 one or the other.</p><li>Your allies are rallied and take +1 forward.</li><li>Your enemies feel fear and act accordingly (avoiding you, hiding, attacking with fear-driven abandon)</li>`,
          have: false
        },
        {
          title: `Mark Of Might `,
          type: `expert`,
          html: `<p>When you take this move and spend some uninterrupted time reflecting on your past glories, you may mark yourself with a symbol of your power (a long braid tied with bells, ritual scars or tattoos, etc.) Any intelligent mortal creature who sees this symbol knows instinctively that you are a force to be reckoned with and treats you appropriately.</p>`,
          have: false
        },
        {
          title: `More! Always More!`,
          type: `expert`,
          html: `<p>When you satisfy an appetite to the extreme (destroying something unique and significant, gaining enormous fame, riches, power, etc.) you may choose to resolve it. Cross it off the list and mark XP. While you may pursue that appetite again, you no longer feel the burning desire you once did. In its place, choose a new appetite from the list or write your own.</p>`,
          have: false
        },
        {
          title: `The One Who Knocks`,
          type: `expert`,
          html: `<p>When you Defy Danger, on a 12+ you turn the danger back on itself, the GM will describe how.</p>`,
          have: false
        },
        {
          title: `Healthy Distrust`,
          type: `expert`,
          html: `<p>Whenever the unclean magic wielded by mortal men causes you to Defy Danger, treat any result of 6- as a 7–9.</p>`,
          have: false
        },
        {
          title: `For The Blood God`,
          type: `expert`,
          html: `<p>You are initiated in the old ways, the ways of sacrifice. Choose something your gods (or the ancestor spirits, or your totem, etc) value—gold, blood, bones or the like. When you sacrifice those things as per your rites and rituals, roll+WIS. • On a 10+ the GM will grant you insight into your current trouble or a boon to help you. • On a 7-9 the sacrifice is not enough and your gods take of your flesh as well, but still grant you some insight or boon. • On a miss, you earn the ire of the fickle spirits.</p>`,
          have: false
        }
      ]
    },
    bard: {
      starting: [
        {
          title: `Arcane Art`,
          type: `starting`,
          html: `<p>When you weave a performance into a basic spell, choose an ally and an effect:</p><li>Heal 1d8 damage</li><li>+1d4 forward to damage</li><li>Their mind is shaken clear of one enchantment</li><li>The next time someone successfully assists the target with aid, they get +2 instead of +1 </li><p>Then roll+CHA. On a 10+, the ally gets the selected effect. On a 7-9, your spell still works, but you draw unwanted attention or your magic reverberates to other targets affecting them as well, GM’s choice.</p>`,
          have: null
        },
        {
          title: `Charming & Open`,
          type: `starting`,
          html: `<p>When you speak frankly with someone, you can ask their player a question from the list below. They must answer it truthfully, then they may ask you a question from the list (which you must answer truthfully).</p><li>Whom do you serve?</li><li>What do you wish I would do?</li><li>How can I get you to _____?</li><li>What are you really feeling right now? • What do you most desire?</li>`,
          have: null
        },
        {
          title: `Bardic Lore`,
          type: `starting`,
          html: `<p>Choose an area of expertise:</p><p style="margin-left: 1em;">Spells and Magicks</p><p style="margin-left: 1em;">The Dead and Undead</p><p style="margin-left: 1em;">Grand Histories of the Known World </p><p style="margin-left: 1em;">A Bestiary of Creatures Unusual </p><p style="margin-left: 1em;">The Planar Spheres</p><p style="margin-left: 1em;">Legends of Heroes Past</p><p style="margin-left: 1em;">Gods and Their Servants</p><p>When you first encounter an important creature, location, or item (your call) covered by your bardic lore you can ask the GM any one question about it; the GM will answer truthfully. The GM may then ask you what tale, song, or legend you heard that information in.</p>`,
          have: null
        },
        {
          title: `A Port In The Storm`,
          type: `starting`,
          html: `<p>When you return to a civilized settlement you’ve visited before, tell the GM when you were last here. They’ll tell you how it’s changed since then.</p>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Healing Song`,
          type: `advanced`,
          html: `<p>When you heal with Arcane Art, you heal +1d8 damage.</p>`,
          have: false
        },
        {
          title: `Vicious Cacophony`,
          type: `advanced`,
          html: `<p>When you grant bonus damage with Arcane Art, you grant an extra +1d4 damage.</p>`,
          have: false
        },
        {
          title: `It Goes To Eleven`,
          type: `advanced`,
          html: `<p>When you unleash a crazed performance (a righteous lute solo or mighy brass blast, maybe) choose a target who can hear you and roll+CHA. • On a 10+ the target attacks their nearest ally in range. • On a 7-9 they attack their nearest ally, but you also draw their attention and ire.</p>`,
          have: false
        },
        {
          title: `Metal Hurlant`,
          type: `advanced`,
          html: `<p>When you shout with great force or play a shattering note choose a target and roll+CON. • On a 10+ the target takes 1d10 damage and is deafened for a few minutes. • On a 7-9 you still damage your target, but it’s out of control: the GM will choose an additional target nearby.</p>`,
          have: false
        },
        {
          title: `A Little Help From My Friends`,
          type: `advanced`,
          html: `<p>When you successfully aid someone you take +1 forward as well.</p>`,
          have: false
        },
        {
          title: `Eldritch Tones`,
          type: `advanced`,
          html: `<p>Your Arcane Art is strong, allowing you to choose two effects instead of one.</p>`,
          have: false
        },
        {
          title: `Duelist’s Parry`,
          type: `advanced`,
          html: `<p>When you Hack & Slash, you take +1 armor forward.</p>`,
          have: false
        },
        {
          title: `Bamboozle`,
          type: `advanced`,
          html: `<p>When you parley with someone, on a 7+ you also take +1 forward with them.</p>`,
          have: false
        },
        {
          title: `Multiclass Dabbler`,
          type: `advanced`,
          html: `<p>Get one move from another class. Treat your level as one lower for choosing the move.</p>`,
          have: false
        }
      ],
      expert: [
        { title: `Healing Chorus`, type: `expert`, html: `<p>(Replaces: Healing Song) When you heal with Arcane Art, you heal +2d8 damage.</p>`, have: false },
        {
          title: `Vicious Blast`,
          type: `expert`,
          html: `<p>(Replaces: Vicious Cacophony) When you grant bonus damage with Arcane Art, you grant an extra +2d4 damage.</p>`,
          have: false
        },
        {
          title: `Unforgettable Face`,
          type: `expert`,
          html: `<p>When you meet someone you’ve met before (your call) after some time apart you take +1 forward against them.</p>`,
          have: false
        },
        {
          title: `Reputation`,
          type: `expert`,
          html: `<p>When you first meet someone who’s heard songs about you, roll+CHA. • On a 10+, tell the GM two things they’ve heard about you. • On a 7-9, tell the GM one thing they’ve heard, and the GM tells you one thing.</p>`,
          have: false
        },
        {
          title: `Eldritch Chord`,
          type: `expert`,
          html: `<p>(Replaces: Eldritch Tones) When you use Arcane Art, you choose two effects. You also get to choose one of those effects to double.</p>`,
          have: false
        },
        {
          title: `An Ear For Magic`,
          type: `expert`,
          html: `<p>When you hear an enemy cast a spell the GM will tell you the name of the spell and its effects. Take +1 forward when acting on the answers.</p>`,
          have: false
        },
        {
          title: `Devious`,
          type: `expert`,
          html: `<p>When you use Charming & Open you may also ask “How are you vulnerable to me?” Your subject may not ask this question of you.</p>`,
          have: false
        },
        {
          title: `Duelist’s Block`,
          type: `expert`,
          html: `<p>(Replaces: Duelist’s Parry) When you Hack & Slash, you take +2 armor forward.</p>`,
          have: false
        },
        {
          title: `Con`,
          type: `expert`,
          html: `<p>(Replaces: Bamboozle) When you parley with someone, on a 7+ you also take +1 forward with them and get to ask their player one question which they must answer truthfully.</p>`,
          have: false
        }
      ]
    },
    cleric: {
      starting: [
        {
          title: `Deity`,
          type: `starting`,
          html: `<p>You serve and worship some deity or power which grants you spells. Give your god a name and choose your deity’s domain:</p><span class="move-property"><input type="checkbox" id="starting-cleric-1"/><p style="margin-left: 1em; text-align: left;">Healing and Restoration</p></span><span class="move-property"><input type="checkbox" id="starting-cleric-2"/><p style="margin-left: 1em; text-align: left;">Bloody Conquest</p></span><span class="move-property"><input type="checkbox" id="starting-cleric-3"/><p style="margin-left: 1em; text-align: left;">Civilization</p></span><span class="move-property"><input type="checkbox" id="starting-cleric-4"/><p style="margin-left: 1em; text-align: left;">Knowledge and Hidden Things </p></span><span class="move-property"><input type="checkbox" id="starting-cleric-5"/><p style="margin-left: 1em; text-align: left;">The Downtrodden and Forgotten </p></span><span class="move-property"><input type="checkbox" id="starting-cleric-6"/><p style="margin-left: 1em; text-align: left;">What Lies Beneath</p></span><p>Choose one precept of your religion:</p><span class="move-property"><input type="checkbox" id="starting-cleric-7"/><p style="margin-left: 1em; text-align: left;">It preaches the sanctity of suffering, add Petition: Suffering</p></span><span class="move-property"><input type="checkbox" id="starting-cleric-8"/><p style="margin-left: 1em; text-align: left;">It’s clutish and insular, add Petition: Gaining Secrets</p></span><span class="move-property"><input type="checkbox" id="starting-cleric-9"/><p style="margin-left: 1em; text-align: left;">It Observes important sacrificial rites, add Petition: Offering</p></span><span class="move-property"><input type="checkbox" id="starting-cleric-10"/><p style="margin-left: 1em; text-align: left;">It believes in trial by combat, add Petition: Personal Victory</p></span>`,
          have: null,
          extra: [
            { type: "checkbox", value: false, marker: "starting-cleric-1" },
            { type: "checkbox", value: false, marker: "starting-cleric-2" },
            { type: "checkbox", value: false, marker: "starting-cleric-3" },
            { type: "checkbox", value: false, marker: "starting-cleric-4" },
            { type: "checkbox", value: false, marker: "starting-cleric-5" },
            { type: "checkbox", value: false, marker: "starting-cleric-6" },
            { type: "checkbox", value: false, marker: "starting-cleric-7" },
            { type: "checkbox", value: false, marker: "starting-cleric-8" },
            { type: "checkbox", value: false, marker: "starting-cleric-9" },
            { type: "checkbox", value: false, marker: "starting-cleric-10" }
          ]
        },
        {
          title: `Divine Guidance`,
          type: `starting`,
          html: `<p>When you petition your deity according to the precept of your religion, you are granted some useful knowledge or boon related to your deity’s domain. The GM will tell you what.</p>`,
          have: null
        },
        {
          title: `Cast A Spell (WIS)`,
          type: `starting`,
          html: `<p>When you unleash a spell granted by your deity, roll+WIS. • On a 10+ the the spell is successfully cast and your deity does not revoke the spell, so you may cast it again.</p><li>On a 7-9 the spell is cast, but choose one:</li><li>You draw unwelcome attention or put yourself in a spot. The GM will tell you how.</li><li>Your casting distances you from your deity; take -1 ongoing to cast a spell until the next time you commune.</li><li>After it is cast, the spell is revoked by your deity. You cannot cast the spell again until you commune and have it granted to you.</li><p>Note that maintaining spells with ongoing effects will sometimes cause a penalty to your roll to cast a spell.</p>`,
          have: null
        },
        {
          title: `Commune`,
          type: `starting`,
          html: `<p>When you spend uninterrupted time (an hour or so) in quiet contemplation with your deity, you:</p><li>Lose any spells already granted to you </li><li>Are granted new spells of your choice whose total levels don’t exceed your own level +1, and none of which is a higher level than your own level</li><li>Prepare all of your rotes, which never count against your limit</li>`,
          have: null
        },
        {
          title: `Turn Undead`,
          type: `starting`,
          html: `<p>When you hold your holy symbol aloft and call on your deity for protection, roll+Wis. • On a 7+, so long as you continue to pray and brandish your holy symbol, no undead may come within reach of you. • On a 10+, you also momentarily daze intelligent undead and cause mindless undead to flee. Aggression breaks the effects and they are able to act as normal.</p>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Chosen One`,
          type: `advanced`,
          html: `<p>Choose one spell. You are granted that spell as if it was one level lower.</p>`,
          have: false
        },
        {
          title: `Invigorate`,
          type: `advanced`,
          html: `<p>When you heal someone they take +2 forward to their damage. </p>`,
          have: false
        },
        {
          title: `The Scales Of Life And Death `,
          type: `advanced`,
          html: `<p>When someone takes their last breath in your presence, they take +1 to the roll.</p>`,
          have: false
        },
        {
          title: `Serenity`,
          type: `advanced`,
          html: `<p>When you cast a spell you ignore the first -1 penalty from ongoing spells.</p>`,
          have: false
        },
        {
          title: `First Aid`,
          type: `advanced`,
          html: `<p>Cure Light Wounds is a rote for you, and therefore doesn’t count against your limit of granted spells.</p>`,
          have: false
        },
        {
          title: `Empower`,
          type: `advanced`,
          html: `<p>When you cast a spell, on a 10+ you have the option of choosing from the 7–9 list. If you do, you may choose one of these effects as well:</p><li>The spell’s effects are doubled </li><li>The spell’s targets are doubled</li>`,
          have: false
        },
        {
          title: `Divine Intervention`,
          type: `advanced`,
          html: `<p>When you Commune you get 1 hold and lose any hold you already had. Spend that hold when you or an ally takes damage to call on your deity, they intervene with an appropriate manifestation (a sudden gust of wind, a lucky slip, a burst of light) and negate the damage.</p>`,
          have: false
        },
        {
          title: `Penitent`,
          type: `advanced`,
          html: `<p>When you take damage and embrace the pain, you may take +1d4 damage (ignoring armor). If you do, take +1 forward to cast a spell.</p>`,
          have: false
        },
        {
          title: `Orison For Guidance`,
          type: `advanced`,
          html: `<p>When you sacrifice something of value to your deity and pray for guidance, your deity tells you what it would have you do. If you do it, mark experience.</p>`,
          have: false
        },
        {
          title: `Divine Protection`,
          type: `advanced`,
          html: `<p>When you wear no armor or shield you get 2 armor.</p>`,
          have: false
        },
        {
          title: `Devoted Healer`,
          type: `advanced`,
          html: `<p>When you heal someone else of damage, add your level to the amount of damage healed.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `Anointed`,
          type: `expert`,
          html: `<p>(Requires: Chosen One) </p><p>Choose one spell in addition to the one you picked for Chosen One. You are granted that spell as if it was one level lower.</p>`,
          have: false
        },
        {
          title: `Apotheosis`,
          type: `expert`,
          html: `<p>The first time you spend time in prayer as appropriate to your god after taking this move, choose a feature associated with your deity (rending claws, wings of sapphire feathers, an all-seeing third eye, etc.). When you emerge from prayer, you permanently gain that physical feature.</p>`,
          have: false
        },
        {
          title: `Reaper`,
          type: `expert`,
          html: `<p>When you take time after a conflict to dedicate your victory to your deity and deal with the dead, take +1 forward.</p>`,
          have: false
        },
        {
          title: `Providence`,
          type: `expert`,
          html: `<p>(Replaces: Serenity) </p><p>You ignore the -1 penalty from up to two spells you maintain. If you maintain more than two you take normal penalties.</p>`,
          have: false
        },
        {
          title: `Divine Invincibility`,
          type: `expert`,
          html: `<p>(Replaces: Divine Intervention) </p><p>When you Commune you gain 2 hold and lose any hold you already had. Spend that hold when you or an ally takes damage to call on your deity, who intervenes with an appropriate manifestation (a sudden gust of wind, a lucky slip, a burst of light) and negates the damage.</p>`,
          have: false
        },
        {
          title: `Martyr`,
          type: `expert`,
          html: `<p>(Replaces: Penitent) </p><p>When you take damage and embrace the pain, you may take +1d4 damage (ignoring armor). If you do, take +1 forward to cast a spell and add your level to any damage done or healed by the spell.</p>`,
          have: false
        },
        {
          title: `Divine Armor`,
          type: `expert`,
          html: `<p>(Replaces: Divine Protection) </p><p>When you wear no armor or shield you get 3 armor.</p>`,
          have: false
        },
        {
          title: `Greater Empower`,
          type: `expert`,
          html: `<p>(Replaces: Empower) </p><p>When you cast a spell, on a 10-11 you have the option of choosing from the 7–9 list. If you do, you may choose one of these effects as well. On a 12+ you get to choose one of these effects for free.</p><li>The spell’s effects are doubled </li><li>The spell’s targets are doubled</li>`,
          have: false
        },
        {
          title: `Greater First Aid`,
          type: `expert`,
          html: `<p>(Requires: First Aid) </p><p>Cure Moderate Wounds is a rote for you, and therefore doesn’t count against your limit of granted spells.</p>`,
          have: false
        },
        {
          title: `Multiclass Dabbler`,
          type: `expert`,
          html: `<p>Get one move from another class. Treat your level as one lower for choosing the move.</p>`,
          have: false
        }
      ]
    },
    druid: {
      starting: [
        {
          title: `Born Of The Soil`,
          type: `starting`,
          html: `<p>You learned your magic in a place whose spirits are strong and ancient and they’ve marked you as one of their own. No matter where you go, they live within you and allow you to take their shape. Choose one of the following. It is the land to which you are attuned—when shapeshifting you may take the shape of any animal who might live in your Land.</p><span class="move-property"><input type="checkbox" id="starting-druid-1"/><p style="margin-left: 1em; text-align: left;">The Great Forests</p></span><span class="move-property"><input type="checkbox" id="starting-druid-2"/><p style="margin-left: 1em; text-align: left;">The Whispering Plains The Vast Desert</p></span><span class="move-property"><input type="checkbox" id="starting-druid-3"/><p style="margin-left: 1em; text-align: left;">The Stinking Mire</p></span><span class="move-property"><input type="checkbox" id="starting-druid-4"/><p style="margin-left: 1em; text-align: left;">The River Delta</p></span><span class="move-property"><input type="checkbox" id="starting-druid-5"/><p style="margin-left: 1em; text-align: left;">The Depths of the Earth </p></span><span class="move-property"><input type="checkbox" id="starting-druid-6"/><p style="margin-left: 1em; text-align: left;">The Sapphire Islands </p></span><span class="move-property"><input type="checkbox" id="starting-druid-7"/><p style="margin-left: 1em; text-align: left;">The Open Sea</p></span><span class="move-property"><input type="checkbox" id="starting-druid-8"/><p style="margin-left: 1em; text-align: left;">The Towering Mountains </p></span><span class="move-property"><input type="checkbox" id="starting-druid-9"/><p style="margin-left: 1em; text-align: left;">The Frozen North</p></span><span class="move-property"><input type="checkbox" id="starting-druid-10"/><p style="margin-left: 1em; text-align: left;">The Blasted Wasteland</p></span><p>Chose a tell—a physical attribute that marks you as born of the soil—that reflects the spirit of your land. It may be an animal feature like antlers or leopard’s spots or something more general: hair like leaves or eyes of glittering crystal. Your tell remains no matter what shape you take.</p>`,
          have: null,
          extra: [
            { type: "checkbox", value: false, marker: "starting-druid-1" },
            { type: "checkbox", value: false, marker: "starting-druid-2" },
            { type: "checkbox", value: false, marker: "starting-druid-3" },
            { type: "checkbox", value: false, marker: "starting-druid-4" },
            { type: "checkbox", value: false, marker: "starting-druid-5" },
            { type: "checkbox", value: false, marker: "starting-druid-6" },
            { type: "checkbox", value: false, marker: "starting-druid-7" },
            { type: "checkbox", value: false, marker: "starting-druid-8" },
            { type: "checkbox", value: false, marker: "starting-druid-9" },
            { type: "checkbox", value: false, marker: "starting-druid-10" }
          ]
        },
        {
          title: `By Nature Sustained`,
          type: `starting`,
          html: `<p>You don’t need to eat or drink. If a move tells you to mark off a ration just ignore it.</p>`,
          have: null
        },
        {
          title: `Spirit Tongue`,
          type: `starting`,
          html: `<p>The grunts, barks, chirps, and calls of the creatures of the wild are as language to you. You can understand any animal native to your land or akin to one whose essence you have studied.</p>`,
          have: null
        },
        {
          title: `Shapeshifter`,
          type: `starting`,
          html: `<p>When you call upon the spirits to change your shape, roll+Wis. • On a 10+ hold 3. • On a 7–9 hold 2. • On a miss hold 1 in addition to whatever the GM says.</p><p>You may take on the physical form of any species whose essence you have studied or who lives in your land: you and your possessions meld into a perfect copy of the species’ form. You have any innate abilities and weaknesses of the form: claws, wings, gills, breathing water instead of air. You still use your normal stats but some moves may be harder to trigger—a housecat will find it hard to do battle with an ogre. The GM will also tell you one or more moves associated with your new form. Spend 1 hold to make that move. Once you’re out of hold, you return to your natural form. At any time, you may spend all your hold and revert to your natural form.</p>`,
          have: null
        },
        {
          title: `Studied Essence`,
          type: `starting`,
          html: `<p>When you spend time in contemplation of an animal spirit, you may add its species to those you can assume using shapeshifting.</p>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Hunter’s Brother`,
          type: `advanced`,
          html: `<p>Choose one move from the ranger class list.</p>`,
          have: false
        },
        {
          title: `Red Of Tooth And Claw `,
          type: `advanced`,
          html: `<p>When you are in an appropriate animal form (something dangerous) increase your damage to d8.</p>`,
          have: false
        },
        {
          title: `Communion Of Whispers `,
          type: `advanced`,
          html: `<p>When you spend time in a place, making note of its resident spirits and calling on the spirits of the land, roll+Wis. You will be granted a vision of significance to you, your allies, and the spirits around you. • On a 10+ the vision will be clear and helpful to you. • On a 7–9 the vision is unclear, its meaning murky. • On a miss, the vision is upsetting, frightening, or traumatizing. The GM will describe it. Take -1 forward.</p>`,
          have: false
        },
        {
          title: `Barkskin`,
          type: `advanced`,
          html: `<p>So long as your feet touch the ground you have +1 armor.</p>`,
          have: false
        },
        {
          title: `Eyes Of The Tiger`,
          type: `advanced`,
          html: `<p>When you mark an animal (with mud, dirt, or blood) you can see through that animal’s eyes as if they were your own, no matter what distance separates you. Only one animal at a time may be marked in this way.</p>`,
          have: false
        },
        {
          title: `Shed`,
          type: `advanced`,
          html: `<p>When you take damage while shapeshifted you may choose to revert to your natural form to negate the damage.</p>`,
          have: false
        },
        {
          title: `Thing-talker`,
          type: `advanced`,
          html: `<p>You see the spirits in the sand, the sea and the stone. You may now apply your spirit tongue, shapeshifting and studied essence to inanimate natural objects (plants and rocks) or creatures made thereof, as well as animals. Thing-talker forms can be exact copies or can be mobile vaguely humanoid-shaped entities.</p>`,
          have: false
        },
        {
          title: `Formcrafter`,
          type: `advanced`,
          html: `<p>When you shapeshift choose a stat: you take +1 ongoing to rolls using that stat while shifted. The GM will choose a stat, too: you take -1 ongoing to rolls using that stat while shifted.</p>`,
          have: false
        },
        {
          title: `Elemental Mastery`,
          type: `advanced`,
          html: `<p>When you call on the primal spirits of fire, water, earth or air to perform a task for you roll+Wis. • On a 10+ choose two. • On a 7–9 choose one. • On a miss, some catastrophe occurs as a result of your calling.</p><li>The effect you desire comes to pass </li><li>You avoid paying nature’s price</li><li>You retain control</li>`,
          have: false
        },
        {
          title: `Balance`,
          type: `advanced`,
          html: `<p>When you deal damage, take 1 balance. When you touch someone and channel the spirits of life you may spend balance. For each balance spent, heal 1d4 HP.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `Embracing No Form`,
          type: `expert`,
          html: `<p>When you shapeshift, roll 1d4 and add that total to your hold.</p>`,
          have: false
        },
        {
          title: `Doppleganger’s Dance`,
          type: `expert`,
          html: `<p>You are able to study the essence of specific individuals to take their exact form, including men, elves, or the like. Suppressing your tell is possible, but if you do, take -1 ongoing until you return to your own form.</p>`,
          have: false
        },
        {
          title: `Blood And Thunder`,
          type: `expert`,
          html: `<p>(Replaces: Red Of Tooth And Claw) When you are in an appropriate animal form (something dangerous) increase your damage to d10.</p>`,
          have: false
        },
        {
          title: `The Druid Sleep`,
          type: `expert`,
          html: `<p>When you take this move, the next opportunity that you have safety and time to spend in an appropriate location, you may attune yourself to a new land. This effect occurs only once and the GM will tell you how long it will take and what cost you must pay. From then on, you are considered to be born of the soil in both lands.</p>`,
          have: false
        },
        {
          title: `Weather Weaver`,
          type: `expert`,
          html: `<p>When you are under open skies when the sun rises the GM will ask you what the weather will be that day. Tell them whatever you like, it comes to pass.</p>`,
          have: false
        },
        {
          title: `World-talker`,
          type: `expert`,
          html: `<p>(Requires: Thing-talker) You see the patterns that make up the fabric of the world. You may now apply your spirit tongue, shapeshifter and studied essence moves to pure elements—fire, water, air and earth.</p>`,
          have: false
        },
        {
          title: `Formshaper`,
          type: `expert`,
          html: `<p>(Requires: Formcrafter) You may increase your armor by 1 or deal an additional +1d4 damage while in an animal form. Choose which when you shapeshift.</p>`,
          have: false
        },
        {
          title: `Healthy Distrust`,
          type: `expert`,
          html: `<p>Whenever the unclean magic wielded by mortal men causes you to Defy Danger, treat any result of 6- as a 7–9.</p>`,
          have: false
        },
        {
          title: `Chimera`,
          type: `expert`,
          html: `<p>When you shapeshift, you may create a merged form of up to three different shapes. You may be a bear with the wings of an eagle and the head of a ram, for example. Each feature will grant you a different move to make. Your chimera form follows the same rules as shapeshifter otherwise.</p>`,
          have: false
        },
        {
          title: `Stalker’s Sister`,
          type: `expert`,
          html: `<p>Choose one move from the ranger class list.</p>`,
          have: false
        }
      ]
    },
    fighter: {
      starting: [
        {
          title: `Signature Weapon`,
          type: `starting`,
          html: `<p>This is your weapon. There are many like it, but this one is yours. Your weapon is your best friend. It is your life. You master it as you master your life. Your weapon, without you, is useless. Without your weapon, you are useless. You must wield your weapon true.</p><p>Choose a base description, all are 2 weight:</p><span class="move-property"><input type="checkbox" id="starting-fighter-1"/><p style="margin-left: 1em; text-align: left;">Sword </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-2"/><p style="margin-left: 1em; text-align: left;">Axe </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-3"/><p style="margin-left: 1em; text-align: left;">Hammer</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-4"/><p style="margin-left: 1em; text-align: left;">Spear </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-5"/><p style="margin-left: 1em; text-align: left;">Flail </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-6"/><p style="margin-left: 1em; text-align: left;">Fists</p></span><p>Choose a range that best fits your weapon:</p><span class="move-property"><input type="checkbox" id="starting-fighter-7"/><p style="margin-left: 1em; text-align: left;">Hand </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-8"/><p style="margin-left: 1em; text-align: left;">Reach</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-9"/><p style="margin-left: 1em; text-align: left;">Close</p></span><p>Choose two enhancements:</p><span class="move-property"><input type="checkbox" id="starting-fighter-10"/><p style="margin-left: 1em; text-align: left;">Hooks and spikes. +1 damage, but +1 weight.</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-11"/><p style="margin-left: 1em; text-align: left;">Sharp. +2 piercing.</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-12"/><p style="margin-left: 1em; text-align: left;">Perfectly weighted. Add precise.</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-13"/><p style="margin-left: 1em; text-align: left;">Serrated edges. +1 damage.</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-14"/><p style="margin-left: 1em; text-align: left;">Glows in the presence of one type of creature, your choice.</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-15"/><p style="margin-left: 1em; text-align: left;">Huge. Add messy and forceful. </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-16"/><p style="margin-left: 1em; text-align: left;">Versatile. Choose an additional range. </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-17"/><p style="margin-left: 1em; text-align: left;">Well-crafted. -1 weight.</p></span><p>Choose a look:</p><span class="move-property"><input type="checkbox" id="starting-fighter-18"/><p style="margin-left: 1em; text-align: left;">Ancient</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-19"/><p style="margin-left: 1em; text-align: left;">Unblemished </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-20"/><p style="margin-left: 1em; text-align: left;">Ornate</p></span><span class="move-property"><input type="checkbox" id="starting-fighter-21"/><p style="margin-left: 1em; text-align: left;">Blood-stained </p></span><span class="move-property"><input type="checkbox" id="starting-fighter-22"/><p style="margin-left: 1em; text-align: left;">Sinister</p></span>`,
          have: null,
          extra: [
            { type: "checkbox", value: false, marker: "starting-fighter-1" },
            { type: "checkbox", value: false, marker: "starting-fighter-2" },
            { type: "checkbox", value: false, marker: "starting-fighter-3" },
            { type: "checkbox", value: false, marker: "starting-fighter-4" },
            { type: "checkbox", value: false, marker: "starting-fighter-5" },
            { type: "checkbox", value: false, marker: "starting-fighter-6" },
            { type: "checkbox", value: false, marker: "starting-fighter-7" },
            { type: "checkbox", value: false, marker: "starting-fighter-8" },
            { type: "checkbox", value: false, marker: "starting-fighter-9" },
            { type: "checkbox", value: false, marker: "starting-fighter-10" },
            { type: "checkbox", value: false, marker: "starting-fighter-11" },
            { type: "checkbox", value: false, marker: "starting-fighter-12" },
            { type: "checkbox", value: false, marker: "starting-fighter-13" },
            { type: "checkbox", value: false, marker: "starting-fighter-14" },
            { type: "checkbox", value: false, marker: "starting-fighter-15" },
            { type: "checkbox", value: false, marker: "starting-fighter-16" },
            { type: "checkbox", value: false, marker: "starting-fighter-17" },
            { type: "checkbox", value: false, marker: "starting-fighter-18" },
            { type: "checkbox", value: false, marker: "starting-fighter-19" },
            { type: "checkbox", value: false, marker: "starting-fighter-20" },
            { type: "checkbox", value: false, marker: "starting-fighter-21" },
            { type: "checkbox", value: false, marker: "starting-fighter-22" }
          ]
        },
        {
          title: `Bend Bars, Lift Gates`,
          type: `starting`,
          html: `<p>When you use pure strength to destroy an</p><p>inanimate obstacle, roll+STR. • On a 10+, choose 3. • On a 7-9 choose 2.</p><li>It doesn’t take a very long time</li><li>Nothing of value is damaged</li><li>It doesn’t make an inordinate amount of noise</li><li>You can fix the thing again without a lot of effort</li>`,
          have: null
        },
        {
          title: `Armored`,
          type: `starting`,
          html: `<p>You ignore the clumsy tag on any armor you wear.</p>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Merciless`,
          type: `advanced`,
          html: `<p>When you deal damage, deal +1d4 damage.</p>`,
          have: false
        },
        {
          title: `Heirloom`,
          type: `advanced`,
          html: `<p>When you consult the spirits that reside within your signature weapon, they will give you an insight relating to the current situation, and might ask you some questions in return, roll+CHA. • On a 10+, the GM will give you good detail. • On a 7-9, the GM will give you an impression.</p>`,
          have: false
        },
        {
          title: `Improved Weapon`,
          type: `advanced`,
          html: `<p>Choose one extra enhancement for your signature weapon.</p>`,
          have: false
        },
        {
          title: `Blacksmith`,
          type: `advanced`,
          html: `<p>When you have access to a forge you can graft the magical powers of a weapon onto your signature weapon. This process destroys the magical weapon. Your signature weapon gains the magical powers of the destroyed weapon.</p>`,
          have: false
        },
        {
          title: `Iron Hide`,
          type: `advanced`,
          html: `<p>You gain +1 armor.</p>`,
          have: false
        },
        {
          title: `Armor Mastery`,
          type: `advanced`,
          html: `<p>When you make your armor take the brunt of damage dealt to you, the damage is negated but you must reduce the armor value of your armor or shield (your choice) by 1. The value is reduced each time you make this choice. If the reduction leaves the item with 0 armor it is destroyed.</p>`,
          have: false
        },
        {
          title: `Seeing Red`,
          type: `advanced`,
          html: `<p>When you Discern Realities during combat, you take +1.</p>`,
          have: false
        },
        {
          title: `Interrogator`,
          type: `advanced`,
          html: `<p>When you parley using threats of violence as leverage, you may use STR instead of CHA.</p>`,
          have: false
        },
        {
          title: `Scent Of Blood`,
          type: `advanced`,
          html: `<p>When you Hack & Slash an enemy, your next attack against that same foe deals +1d4 damage.</p>`,
          have: false
        },
        {
          title: `Multiclass Dabbler`,
          type: `advanced`,
          html: `<p>Get one move from another class. Treat your level as one lower for choosing the move.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `Bloodthirsty`,
          type: `expert`,
          html: `<p><em>Replaces: Merciless</em></p><p>When you deal damage, deal +1d8 damage.</p>`,
          have: false
        },
        {
          title: `Through Death’s Eyes`,
          type: `expert`,
          html: `<p>When you go into battle, roll+WIS. • On a 10+, name someone who will live and someone who will die. • On a 7-9, name someone who will live or someone who will die. Name NPCs, not player characters. The GM will make your vision come true, if it’s even remotely possible. • On a 6- you see your own death and consequently take -1 ongoing throughout the battle.</p>`,
          have: false
        },
        {
          title: `Eye For Weaponry `,
          type: `expert`,
          html: `<p>When you look over an enemy’s weaponry, ask the GM how much damage they do.</p>`,
          have: false
        },
        {
          title: `Superior Warrior`,
          type: `expert`,
          html: `<p>When you Hack & Slash, on a 12+ you deal your damage, avoid their attack, and impress, dismay, or frighten your enemy.</p>`,
          have: false
        },
        {
          title: `Steel Hide`,
          type: `expert`,
          html: `<p><em>Replaces: Iron Hide</em></p><p>You gain +2 armor.</p>`,
          have: false
        },
        {
          title: `Armored Perfection`,
          type: `expert`,
          html: `<p><em>Replaces: Armor Mastery</em></p><p>When you choose to let your armor take the brunt of damage dealt to you, the damage is negated and you take +1 forward against the attacker, but you must reduce the armor value of your armor or shield (your choice) by 1. The value is reduced each time you make this choice. If the reduction leaves the item with 0 armor it is destroyed.</p>`,
          have: false
        },
        {
          title: `Evil Eye`,
          type: `expert`,
          html: `<p><em>Requires: Seeing Red</em></p><p>When you enter combat, roll+CHA. • On a 10+, hold 2. • On a 7-9, hold 1. Spend your hold to make eye contact with an NPC present, who freezes or flinches and can’t act until you break it off. • On a 6-, your enemies immediately identify you as their biggest threat.</p>`,
          have: false
        },
        {
          title: `Taste Of Blood`,
          type: `expert`,
          html: `<p><em>Replaces: Scent Of Blood</em></p><p>When you Hack & Slash an enemy, your next attack against that same foe deals +1d8 damage.</p>`,
          have: false
        },
        {
          title: `Multiclass Initiate`,
          type: `expert`,
          html: `<p>Get a move from another class. Treat your level as one lower for choosing the move.</p>`,
          have: false
        }
      ]
    },
    immolator: {
      starting: [
        {
          title: `Burning Brand`,
          type: `starting`,
          html: `<p>When you conjure a weapon of pure flame, roll+CON. On a 10+ choose two of the following tags, on a 7-9 choose one. You may treat your INT as your STR or DEX in regards to making attacks with this weapon. The weapon always begins with the fiery, touch, dangerous, and 3 uses tags. Each attack with the weapon consumes one use.</p><li>hand</li><li>thrown, near</li><li>+1 damage</li><li>remove the dangerous tag</li>`,
          have: null
        },
        {
          title: `Fighting Fire with Fire`,
          type: `starting`,
          html: `<p>When you take damage, and that damage is odd (after armor) the flames within you come to your aid. Roll 1d4 and either add that many uses to your burning brand (if active), take that result forward to summon your burning brand, or reduce the damage by that amount, your choice.</p>`,
          have: null
        },
        {
          title: `Give Me Fuel, Give Me Fire `,
          type: `starting`,
          html: `<p>When you gaze intensely into someones eyes, you may ask their player “what fuels the flames of your desire?” they’ll answer with the truth, even if the character does not know or would otherwise keep this hidden.</p>`,
          have: null
        },
        {
          title: `Zuko Style`,
          type: `starting`,
          html: `<p>When you bend a flame to your will, roll+WIS. On a 10+ it does as you command, taking the shape and movement you desire for as long as it has fuel on which to burn. On a 7-9 the effect is short-lived, lasting only a moment.</p>`,
          have: null
        },
        {
          title: `Hand Crafted`,
          type: `starting`,
          html: `<p>You may use your hands in place of tools and fire to craft metal objects. Mundane weapons, armor and metal jewellery can all be formed from their raw components. You may unmake these things, as well, but to do so without time and safety might require that you Defy Danger first.</p>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Lore Of Flame`,
          type: `advanced`,
          html: `<p>When you stare into a source of fire, looking for answers, roll+WIS. On a hit, the GM will tell you something new and interesting about the current situation. On a 10+, the GM will give you good detail. On a 7–9, the GM will give you an impression. If you already know all there is to know, the GM will tell you that.</p>`,
          have: false
        },
        {
          title: `Burns Twice As Bright`,
          type: `advanced`,
          html: `<p>When you channel the flames of fate, you may treat a missed roll as a 7-9 or a 7-9 result as a 10+. This may be a roll you or another character has made. Tell the GM something you’ve lost; an emotion, a memory or some innate piece of your being. You may not use this move again until you’ve used Burns Half As Long.</p>`,
          have: false
        },
        {
          title: `Burns Half As Long`,
          type: `advanced`,
          html: `<p>You gain this move when you gain Burns Twice As Bright When you sacrifice a victory to the flames of fate, treat any roll of 10+ as a miss.</p>`,
          have: false
        },
        {
          title: `This Killing Fire`,
          type: `advanced`,
          html: `<p>Add the following tags to your options for Burning Brand: messy, forceful, reach, near, far.</p>`,
          have: false
        },
        {
          title: `Firebrand`,
          type: `advanced`,
          html: `<p>When you introduce a new idea to an NPC, roll+CHA. On a 10+ They believe the idea to be their own and take to it with fervour On a 7-9, Their passion fades after a day or two. On a miss, they respond negatively, speaking out against the idea.</p>`,
          have: false
        },
        {
          title: `Ogdru Jahad`,
          type: `advanced`,
          html: `<p>Gain the Wizard move Ritual. The GM will always tell you what you have to sacrifice to gain the effect you desire.</p>`,
          have: false
        },
        {
          title: `Moth To The Flame`,
          type: `advanced`,
          html: `<p>When you tempt a weak mind with your inner fire, roll+WIS. On a 10+ their will is suppressed, they’ll follow you and do as you desire, so long as nothing startles or surprises them. On a 7-9, the effect is only strong enough to distract or confuse them. On a miss, they become agitated and upset, your fire having sparked their hidden desires.</p>`,
          have: false
        },
        {
          title: `Burning Bridges`,
          type: `advanced`,
          html: `<p>When you would take your last breath, don’t. Instead, you may erase one of your Bonds. This is permanent and lowers your total available Bonds forever. You are alive and have 1d6 hp. If you have no more Bonds, take your last breath as normal.</p>`,
          have: false
        },
        {
          title: `The Enkindler`,
          type: `advanced`,
          html: `<p>When you bolster the courage of others roll+CHA. On a 10+ they shake off all fear and doubt, becoming brave in an instant. On a 7-9, this effect is fleeting, they realize its superficiality and resort to cowardice after a moment or two. On a miss, they’re cowed or terrified by your presence.</p>`,
          have: false
        },
        {
          title: `Sick Burn`,
          type: `advanced`,
          html: `<p>When you insult an NPC, roll + CHA. On a 10+ you leave them no room to react, they bear your insult and the scorn of all who hear it. On a 7-9 you cross a line, they will have their revenge, someday. On a miss you've gone too far, they blow up here and now.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `From Hell’s Heart`,
          type: `expert`,
          html: `<p>When you summon fire with any of your moves, you can replace it with the black fires of hell itself. This fire does not burn with heat and ignores armor, scorching the soul itself. Those creatures without souls cannot be harmed by this type of flame.</p>`,
          have: false
        },
        {
          title: `Burning Ring Of Fire`,
          type: `expert`,
          html: `<p>When you fuse a willing person’s soul to yours, roll+CHA. On a hit you are bound together, able to sense each other at any distance, as well as sharing your emotional state. On a 7-9, the connection is unstable and dangerous, when you take a debility, so do they (and vice versa). On a miss, the branding is rejected and you both erase any Bonds you have to each other. You may write new Bonds with them at the End of Session as usual. This fusion, once performed, cannot be undone by mortal means.</p>`,
          have: false
        },
        {
          title: `Fanning The Flames`,
          type: `expert`,
          html: `<p>(Requires: Firebrand) You may apply the effects of your Firebrand move to a group of people - a dozen or so - all at once.</p>`,
          have: false
        },
        {
          title: `Watch The World Burn`,
          type: `expert`,
          html: `<p>When you open a channel to the burning planes and call a firestorm, tell the GM what you’re sacrificing and roll+WIS. The sky opens up and fire pours like rain from it within an area about equal to a small village. Everyone and everything in the area takes damage as appropriate. On a 10+ you can extinguish the storm with a little effort. On a 7-9 the fires rage out of your control, spreading and gusting where they are carried by wind and weather. On a miss, something cruel, intelligent and hungry comes with the storm.</p>`,
          have: false
        }
      ]
    },
    paladin: {
      starting: [
        { title: `Quest`, type: `starting`, html: `<p>When you dedicate yourself to a mission through prayer and ritual cleansing, state what you set out to do:</p><li>Slay <input type="text" class="moves-text-input" id="starting-paladin-1" />, a great blight on the land.</li><li>Defend <input type="text" class="moves-text-input" id="starting-paladin-2" /> from the iniquities that beset them.</li><li>Discover the truth of <input type="text" class="moves-text-input" id="starting-paladin-3" /></li><p>Then choose up to two boons:</p><li>An unwavering sense of direction to .</li><li>Invulnerability to (edged weapons, fire, enchantment, etc.)</li><li>A mark of divine authority.</li><li>Senses that pierce lies.</li><li>A voice that transcends language.</li><li>Freedom from hunger, thirst, and sleep.</li><p>The GM will then tell you what vow or vows is required of you to maintain your blessing:</p><li>Honor (forbidden: cowardly tactics and tricks)</li><li>Temperance (forbidden: gluttony in food, drink, and pleasure of the flesh)</li><li>Piety (required: observance of daily holy services)</li><li>Valor (forbidden: suffering an evil creature to live)</li><li>Truth (forbidden: lies)</li><li>Hospitality (required: comfort to those in need, no matter who they are)</li>`, have: null, extra: [{ "type": "text", "value": "", "marker": "starting-paladin-1" }, { "type": "text", "value": "", "marker": "starting-paladin-2" }, { "type": "text", "value": "", "marker": "starting-paladin-3" }] },
        { title: `Lay On Hands (CHA)`, type: `starting`, html: `<p>When you touch someone, skin to skin, and pray for their well-being, roll+CHA. • On a 10+ you heal 1d8 damage or remove one disease. • On a 7–9, they are healed, but the damage or disease is transferred to you.</p>`, have: null },
        { title: `I Am The Law`, type: `starting`, html: `<p>When you give an NPC an order based on your divine authority, roll+Cha. • On a 7+, they choose one:</p><li>Do what you say.</li><li>Back away cautiously, then flee. </li><li>Attack you.</li><p> • On a 10+, you also take +1 forward against them. • On a miss, they do as they please and you take -1 forward against them.</p>`, have: null },
        { title: `Armored`, type: `starting`, html: `<p>You ignore the clumsy tag on armor you wear.</p>`, have: null },
      ],
      advanced: [
        { title: `Divine Favor`, type: `advanced`, html: `<p>Dedicate yourself to a deity (name a new one or choose one that’s already been established). You gain the commune and cast a spell cleric moves. When you select this move, treat yourself as a cleric of level 1 for using spells. Every time you gain a level thereafter, increase your effective cleric level by 1.</p>`, have: false },
        { title: `Bloody Aegis`, type: `advanced`, html: `<p>When you take damage you can grit your teeth and accept the blow. If you do you take no damage but instead suffer a debility of your choice. If you already have all six debilities you can’t use this move.</p>`, have: false },
        { title: `Smite`, type: `advanced`, html: `<p>While on a Quest you deal +1d4 damage.</p>`, have: false },
        { title: `Holy Protection`, type: `advanced`, html: `<p>You get +1 armor while on a Quest.</p>`, have: false },
        { title: `Voice Of Authority`, type: `advanced`, html: `<p>Take +1 to order hirelings.</p>`, have: false },
        { title: `Hospitaller`, type: `advanced`, html: `<p>When you heal an ally, you heal +1d8 damage.</p>`, have: false },
        { title: `Exterminatus`, type: `advanced`, html: `<p>When you speak aloud your promise to defat an enemy, you deal +2d4 damage against that enemy and -4 damage against anyone else. This effect lasts until the enemy is defeated. If you fail to defeat the enemy or give up the fight, you can admit your failure, but the effect continues until you find a way to redeem yourself.</p>`, have: false },
        { title: `Charge!`, type: `advanced`, html: `<p>When you lead the charge into combat, those you lead take +1 forward.</p>`, have: false },
        { title: `Staunch Defender`, type: `advanced`, html: `<p>When you Defend you always get +1 hold, even on a 6-.</p>`, have: false },
        { title: `Setup Strike`, type: `advanced`, html: `<p>When you Hack & Slash, choose an ally. Their next attack against your target does +1d4 damage.</p>`, have: false },
      ],
      expert: [
        { title: `Evidence Of Faith`, type: `expert`, html: `<p><em>Requires: Divine Favor</em></p><p>When you see divine magic as it happens, you can ask the GM which deity granted the spell and its effects. Take +1 when acting on the answers.</p>`, have: false },
        { title: `Holy Smite`, type: `expert`, html: `<p><em>Replaces: Smite</em></p><p>While on a Quest you deal +1d8 damage.</p>`, have: false },
        { title: `Ever Onward`, type: `expert`, html: `<p><em>Replaces: Charge!</em></p><p>When you lead the charge into combat, those you lead take +1 forward and +2 armor forward.</p>`, have: false },
        { title: `Impervious Defender`, type: `expert`, html: `<p><em>Replaces: Staunch Defender</em></p><p>When you Defend you always get +1 hold, even on a 6-. When you get a 12+ to defend instead of getting hold the nearest attacking creature is stymied giving you a clear advantage, the GM will describe it.</p>`, have: false },
        { title: `Perfect Knight`, type: `expert`, html: `<p>When you Quest you choose three boons instead of two.</p>`, have: false },
        { title: `Tandem Strike`, type: `expert`, html: `<p><em>Replaces: Setup Strike</em></p><p>When you Hack & Slash, choose an ally. Their next attack against your target does +1d4 damage and they take +1 forward against them.</p>`, have: false },
        { title: `Divine Protection`, type: `expert`, html: `<p><em>Replaces: Holy Protection</em></p><p>You get +2 armor while on a Quest.</p>`, have: false },
        { title: `Divine Authority`, type: `expert`, html: `<p><em>Replaces: Voice Of Authority</em></p><p>Take +1 to order hirelings. When you roll a 12+ the hireling transcends their moment of fear and doubt and carries out your order with particular effectiveness or efficiency.</p>`, have: false },
        { title: `Perfect Hospitaller`, type: `expert`, html: `<p><em>Replaces: Hospitaller</em></p><p>When you heal an ally, you heal +2d8 damage.</p>`, have: false },
        { title: `Indomitable`, type: `expert`, html: `<p>When you suffer a debility (even through Bloody Aegis) take +1 forward against whatever caused it.</p>`, have: false },
      ],
    },
    ranger: {
      starting: [],
      advanced: [],
      expert: [],
    },
    thief: {
      starting: [
        {
          title: `Trap Expert`,
          type: `starting`,
          html: `<p>When you spend a moment to survey a dangerous area, roll+DEX. • On a 10+, hold 3. • On a 7–9, hold 1. Spend your hold as you walk through the area to ask these questions:</p><li>Is there a trap here and if so, what activates it?</li><li>What does the trap do when activated? </li><li>What else is hidden here?</li>`,
          have: null
        },
        {
          title: `Tricks Of The Trade`,
          type: `starting`,
          html: `<p>When you pick locks or pockets or disable traps, roll+DEX. • On a 10+, you do it, no problem. • On a 7–9, you still do it, but the GM will offer you two options between suspicion, danger, or cost.</p>`,
          have: null
        },
        {
          title: `Backstab`,
          type: `starting`,
          html: `<p>When you attack a surprised or defenseless enemy with a melee weapon, you can choose to deal your damage or roll+DEX. • On a 10+, choose two. • On a 7–9, choose one:</p><li>You don’t get into melee with them</li><li>You deal your damage+1d6</li><li>You create an advantage, +1 forward to you or an ally acting on it</li><li>Reduce their armor by 1 until they repair it</li>`,
          have: null
        },
        {
          title: `Flexible Morals`,
          type: `starting`,
          html: `<p>When someone tries to detect your alignment you can tell them any alignment you like.</p>`,
          have: null
        },
        {
          title: `Poisoner`,
          type: `starting`,
          html: `<p>You’ve mastered the care and use of a poison. Choose a poison from the list below; that poison is no longer dangerous for you to use. You also start with three uses of the poison you choose. Whenever you have time to gather materials and a safe place to brew you can make three uses of the poison you choose for free. Note that some poisons are applied, meaning you have to carefully apply it to the target or something they eat or drink. Touch poisons just need to touch the target, they can even be used on the blade of a weapon.</p><li>Oil of Tagit: (applied) The target falls into a light sleep.</li><li>Bloodweed: (touch) The target deals -1d4 damage ongoing until cured.</li><li>Goldenroot: (applied) The target treats the next creature they see as a trusted ally, until proved otherwise.</li><li>Serpent’s Tears: (touch) Anyone dealing damage to the target rolls twice and takes the better result.</li>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Cheap Shot`,
          type: `advanced`,
          html: `<p>When using a precise or hand weapon, your Backstab deals an extra +1d6 damage.</p>`,
          have: false
        },
        {
          title: `Cautious`,
          type: `advanced`,
          html: `<p>When you use Trap Expert you always get +1 hold, even on a 6-.</p>`,
          have: false
        },
        {
          title: `Wealth And Taste`,
          type: `advanced`,
          html: `<p>When you make a show of flashing around your most valuable possession, choose someone present. They will do anything they can to obtain your item or one like it.</p>`,
          have: false
        },
        {
          title: `Shoot First`,
          type: `advanced`,
          html: `<p>You’re never caught by surprise. When an enemy would get the drop on you, you get to act first instead.</p>`,
          have: false
        },
        {
          title: `Underdog`,
          type: `advanced`,
          html: `<p>When you’re outnumbered, you have +1 armor.</p>`,
          have: false
        },
        {
          title: `Poison Master`,
          type: `advanced`,
          html: `<p>After you’ve used a poison once it’s no longer dangerous for you to use.</p>`,
          have: false
        },
        {
          title: `Envenom`,
          type: `advanced`,
          html: `<p>You can apply even complex poisons with a pinprick. When you apply a poison that’s not dangerous for you to use to your weapon it’s touch instead of applied.</p>`,
          have: false
        },
        {
          title: `Brewer`,
          type: `advanced`,
          html: `<p>When you have time to gather materials and a safe place to brew you can create three doses of any one poison you’ve used before.</p>`,
          have: false
        },
        {
          title: `Connections`,
          type: `advanced`,
          html: `<p>When you put out word to the criminal underbelly about something you want or need, roll+CHA. • On a 10+, someone has it, just for you. • On a 7–9, you’ll have to settle for something close or it comes with strings attached, your call.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `Dirty Fighter`,
          type: `expert`,
          html: `<p>(Replaces: Cheap Shot) When using a precise or hand weapon, your Backstab deals an extra +1d8 damage and all other attacks deal +1d4 damage.</p>`,
          have: false
        },
        {
          title: `Extremely Cautious`,
          type: `expert`,
          html: `<p>(Replaces: Cautious) When you use use Trap Expert you always get +1 hold, even on a 6-. On a 12+ you get 3 hold and the next time you come near a trap the GM will immediately tell you what it does, what triggers it, who set it, and how you can use it to your advantage.</p>`,
          have: false
        },
        {
          title: `Serious Underdog`,
          type: `expert`,
          html: `<p>(Replaces: Underdog) You have +1 armor. When you’re outnum- bered, you have +2 armor instead.</p>`,
          have: false
        },
        {
          title: `Evasion`,
          type: `expert`,
          html: `<p>When you Defy Danger on a 12+, you transcend the danger. You not only do what you set out to, but the GM will offer you a better outcome, true beauty, or a moment of grace.</p>`,
          have: false
        },
        {
          title: `Heist`,
          type: `expert`,
          html: `<p>(When you take time to make a plan to) steal something, name the thing you want to steal and ask the GM these questions. When acting on the answers you and your allies take +1 forward.</p><li>Who will notice it’s missing?</li><li>What’s its most powerful defense? </li><li>Who will come after it?</li><li>Who else wants it?</li>`,
          have: false
        },
        {
          title: `Alchemist`,
          type: `expert`,
          html: `<p>(Replaces: Brewer) When you have time to gather materials and a safe place to brew you can create three doses of any poison you’ve used before. Alternately you can describe the effects of a poison you’d like to create. The GM will tell you that you can create it, but with one or more caveats:</p><li>It will only work under specific circumstances.</li><li>The best you can manage is a weaker version.</li><li>It’ll take a while to take effect. </li><li>It’ll have obvious side effects.</li>`,
          have: false
        },
        {
          title: `Escape Route`,
          type: `expert`,
          html: `<p>When you’re in too deep and need a way out, name your escape route and roll+DEX. • On a 10+ you’re gone. • On a 7–9 you can stay or go, but if you go it costs you: leave something behind or take something with you, the GM will tell you what.</p>`,
          have: false
        },
        {
          title: `Disguise`,
          type: `expert`,
          html: `<p>When you have time and materials you can create a disguise that will fool anyone into thinking you’re another creature of about the same size and shape. Your actions can give you away but your appearance won’t</p>`,
          have: false
        },
        {
          title: `Strong Arm, True Aim`,
          type: `expert`,
          html: `<p>You can throw any melee weapon, using it to volley. A thrown melee weapon is gone; you can never choose to reduce ammo on a 7–9.</p>`,
          have: false
        }
      ]
    },
    wizard: {
      starting: [
        {
          title: `Cast A Spell (INT)`,
          type: `starting`,
          html: `<p>When you release a spell you’ve prepared, roll+INT. • On a 10+ the the spell is successfully cast and you may cast the spell again later. • On a 7-9 the spell is cast, but choose one:</p><li>You draw unwelcome attention or put yourself in a spot. The GM will tell you how.</li><li>The spell disturbs the fabric of reality as it is cast; take -1 ongoing to cast a spell until the next time you Prepare Spells.</li><li>After it is cast, the spell is forgotten. You cannot cast the spell again until you prepare spells.</li><p>Note that maintaining spells with ongoing effects will sometimes cause a penalty to your roll to cast a spell.</p>`,
          have: null
        },
        {
          title: `Prepare Spells`,
          type: `starting`,
          html: `<p>When you spend uninterrupted time (an hour or so) in quiet contemplation of your spellbook, you:</p><li>Lose any spells you already prepared</li><li>Prepare new spells chosen from your spellbook whose total levels don’t exceed your own level +1</li><li>Prepare your cantrips which never count against your limit</li>`,
          have: null
        },
        {
          title: `Spellbook`,
          type: `starting`,
          html: `<p>You have mastered several spells and inscribed them in your spellbook. You start out with three first level spells, as well as all your cantrips. Whenever you gain a level, you inscribe a new spell of your level or lower. Your spellbook is 1 weight.</p>`,
          have: null
        },
        {
          title: `Spell Defense`,
          type: `starting`,
          html: `<p>You may end any ongoing spell immediately and use the energy of its dissipation to deflect an oncoming attack. The spell ends and you subtract its level from the damage done to you.</p>`,
          have: null
        },
        {
          title: `Ritual`,
          type: `starting`,
          html: `<p>When you draw on a place of power to create a magical effect, tell the GM what you’re trying to achieve. Ritual effects are always possible, but the GM will give you one to four of the following conditions:</p><li>It’s going to take days/weeks/months • First you must ____</li><li>You’ll need help from ____</li><li>It will require a lot of money</li><li>The best you can do is a lesser version, unreliable and limited</li><li>You and your allies will risk danger from ____</li><li>You’ll have to disenchant ____ to do it</li>`,
          have: null
        }
      ],
      advanced: [
        {
          title: `Prodigy`,
          type: `advanced`,
          html: `<p>Choose a spell. You prepare that spell as if it were one level lower.</p>`,
          have: false
        },
        {
          title: `Empowered Magic`,
          type: `advanced`,
          html: `<p>When you cast a spell, on a 10+ you have the option of choosing from the 7-9 list. If you do, you may additionally choose one of the following effects:</p><li>The spell’s effects are maximized </li><li>The spell’s targets are doubled</li>`,
          have: false
        },
        {
          title: `Fount Of Knowledge`,
          type: `advanced`,
          html: `<p>When you spout lore about something no one else has any clue about, take +1.</p>`,
          have: false
        },
        {
          title: `Know-It-All`,
          type: `advanced`,
          html: `<p>When another player’s character comes to you for advice and you tell them what you think is best, they get +1 forward when following your advice and you mark experience if they do.</p>`,
          have: false
        },
        {
          title: `Expanded Spellbook`,
          type: `advanced`,
          html: `<p>Add a new spell from the spell list of any class to your spellbook.</p>`,
          have: false
        },
        {
          title: `Enchanter`,
          type: `advanced`,
          html: `<p>When you have time and safety with a magic item you may ask the GM what it does. The GM will answer truthfully.</p>`,
          have: false
        },
        {
          title: `Logical`,
          type: `advanced`,
          html: `<p>When you use strict deduction to analyse your surroundings, you can Discern Realities with INT instead of WIS.</p>`,
          have: false
        },
        {
          title: `Arcane Ward`,
          type: `advanced`,
          html: `<p>As long as you have at least one prepared spell of first level or higher, you have +2 armor.</p>`,
          have: false
        },
        {
          title: `Counterspell`,
          type: `advanced`,
          html: `<p>When you attempt to counter an arcane spell that will otherwise affect you, stake one of your prepared spells on the defense and roll+INT. On a 10+, the spell is countered and has no effect on you. On a 7-9, the spell is countered and you forget the spell that you staked. Your counterspell protects you alone; if the countered spell has other targets they are affected as normal.</p>`,
          have: false
        },
        {
          title: `Quick Study`,
          type: `advanced`,
          html: `<p>When you see the effects of an arcane spell, ask the GM the name of the spell and its effects. You take +1 when acting on the answers.</p>`,
          have: false
        }
      ],
      expert: [
        {
          title: `Master`,
          type: `expert`,
          html: `<p><em>Requires: Prodigy</em></p><p>Choose one spell in addition to the one you picked for Prodigy. You prepare that spell as if it were one level lower.</p>`,
          have: false
        },
        {
          title: `Greater Empowered Magic`,
          type: `expert`,
          html: `<p><em>Replaces: Empowered Magic</em></p><p>When you cast a spell, on a 10-11 you have the option of choosing from the 7-9 list. If you do, you may additionally choose one of the following effects. On a 12+ you get to choose one of these effects for free:</p><li>The spell’s effects are maximized </li><li>The spell’s targets are doubled</li>`,
          have: false
        },
        {
          title: `Enchanter’s Soul`,
          type: `expert`,
          html: `<p><em>Requires: Enchanter</em></p><p>When you have time and safety with a magic item in a place of power you can empower that item so that the next time you use it its effects are amplified, the GM will tell you exactly how.</p>`,
          have: false
        },
        {
          title: `Highly Logical`,
          type: `expert`,
          html: `<p><em>Replaces: Logical</em></p><p>When you use strict deduction to analyse your surroundings, you can Discern Realities with INT instead of WIS. On a 12+ you get to ask the GM any three questions, not limited by the list.</p>`,
          have: false
        },
        {
          title: `Mystical Puppet Strings`,
          type: `expert`,
          html: `<p><em>When you use magic to control a person’s actions, they have no memory of what you had them do and bear you no ill will.</em></p>`,
          have: false
        },
        {
          title: `Arcane Armor`,
          type: `expert`,
          html: `<p><em>Replaces: Arcane Ward</em></p><p>As long as you have at least one prepared spell of first level or higher, you have +4 armor.</p>`,
          have: false
        },
        {
          title: `Protective Counter`,
          type: `expert`,
          html: `<p><em>Requires: Counterspell</em></p><p>When an ally within sight of you is affected by an arcane spell, you can counter it as if it affected you. If the spell affects multiple allies you must counter for each ally separately.</p>`,
          have: false
        },
        {
          title: `Ethereal Tether`,
          type: `expert`,
          html: `<p>When you have time with a willing or helpless subject you can craft an ethereal tether with them. You perceive what they do and can discern realities about them or their surroundings, no matter the distance. Someone willingly tethered to you can communicate with you over the tether as if you were in the room with them.</p>`,
          have: false
        },
        {
          title: `Spell Augmentation`,
          type: `expert`,
          html: `<p>When you deal damage to a creature, you can shunt a spell’s energy into them—end one of your ongoing spells and add the spell’s level to the damage dealt.</p>`,
          have: false
        },
        {
          title: `Self-Powered`,
          type: `expert`,
          html: `<p>When you have time, arcane materials, and a safe space, you can create your own place of power. Describe to the GM what kind of power it is and how you’re binding it to this place, the GM will tell you one kind of creature that will have an interest in this.</p>`,
          have: false
        }
      ]
    },
    channeler: {
      starting: [{ title: `Trickle`, type: `starting`, html: `<p>You can emit a small amount of your Primary Element for mundane tasks or to impress someone without having to Channel.</p>`, have: null },
      { title: `Channel`, type: `starting`, html: `<p>When you open your Conduit up wide and channel magic through your body, roll +CON. ✴On a 10+, raw magic of your Primary Element flows out of your Emanation Point. Use one of your Techniques. ✴On a 7-9, the same, but the power is too much. You can use your body as a dam and take 1d4 damage (ignores armor) or allow the power through. If you allow the power through, the GM will tell you of one or more complications, such as:</p><li>The magic flows out of a different part of your body or is of a different element </li><li>The magic has an unintended effect on the environment</li><li>The magic causes a blast wave and you are thrown backwards</li>`, have: null },
      { title: `Techniques`, type: `starting`, html: `<p>You have special Techniques that allow limited control over the flow of magic. When you spend several hours developing a new Technique, add it to your list or replace an existing one. You can learn up to 3 Techniques.</p><p>Keeping in mind your Primary Element, name the Technique and choose 2 tags:</p><li>Projectile (1d6 damage if Near) </li><li>Blast (1d8 damage if Close)</li><li>2 Piercing</li><li>Barrier (+1 Armor until next Channel) </li><li>Hazard (Environmental effect)</li><li>Forceful</li><p>Current Techniques</p><p><input type="text" class="moves-text-input" id="starting-channeler-1" /></p><p><input type="text" class="moves-text-input" id="starting-channeler-2" /></p><p><input type="text" class="moves-text-input" id="starting-channeler-3" /></p><p><input type="text" class="moves-text-input" id="starting-channeler-4" /></p>`, have: null, extra: [{ "type": "text", "value": "", "marker": "starting-channeler-1" }, { "type": "text", "value": "", "marker": "starting-channeler-2" }, { "type": "text", "value": "", "marker": "starting-channeler-3" }, { "type": "text", "value": "", "marker": "starting-channeler-4" }] },
      { title: `Elemental Protection`, type: `starting`, html: `<p>When you or your leashed Elemental takes damage from any source, you lose 1 Control and the damage is negated.</p>`, have: null },
      { title: `Elemental AttacK`, type: `starting`, html: `<p>When you command your leashed Elemental to use its tag, roll+Control. ✴On a 10+, success. ✴On a 7-9, you also lose 1 Control.</p>`, have: null },
      { title: `Summon Elemental`, type: `starting`, html: `<p>When you beckon inwards to call forth an Elemental being, choose any Technique tag and roll+CON. ✴On a 10+, the Elemental flows out of you and a stream of magic connects it to your Emanation Point like a leash. The Elemental's nature and appearance wholly embodies the tag you chose. You gain 1d4 Control.</p><p>✴On a 7-9, the Elemental is also moody or troublesome, choose one: </p><li>You must immediately spend 1 Control</li><li>It embodies a different tag instead (the GM will tell you which) </li><li>It resents you and will certainly be hostile later</li><p>As long as the Elemental remains leashed, you have the Elemental Protection and Elemental Attack moves.</p><p>When you have 0 Control, your elemental is released! It acts on its own, can use its tag at will, and its HP is 2x your level. Maybe it's friendly, hostile, mischievous, or just bored.</p>`, have: null },
      ],
      advanced: [],
      expert: [],
    }
  }
};

export default defaultState;
